import React, { useEffect, useState } from "react";
import styles from "./style.module.scss";
import {
  Avatar,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { Icons } from "assets";
import DeleteIcon from "@mui/icons-material/Delete";
import useWindowSize from "shared/customHooks/windowSize";
import Pagination from "shared/components/pagination";
import ArticleIcon from "@mui/icons-material/Article";
import { routeConstant } from "shared/routes/routesConstant";
import { useNavigate } from "react-router-dom";
import Confirmation from "shared/modals/confirmation";
import {
  allReportedUsersService,
  deleteRequestService,
} from "shared/services/projectHeadService";
import { toastMessage } from "shared/components/toast";
import SkeletonLoader from "./skeletonLoader";
import useDebounce from "shared/customHooks/useDebounce";
import ReportIcon from "@mui/icons-material/Report";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import { getPublishedBy } from "shared/utils/helpers";
import ItemsPerPage from "shared/components/itemsPerPage";
import { user_roles } from "shared/utils/constants";

function ReportedUsers() {
  const screenWidth = useWindowSize();
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState<number>(1);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [allUsers, setAllUsers] = useState<any>(null);
  const [search, setSearch] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [totalPage, setTotalPage] = useState<number>(0);
  // Starts true: getAllReportedUsers() runs from a useEffect, which fires only
  // after the first render commits. Starting false paints "No Data Found" for
  // a frame (the list is still empty) before the skeleton ever appears.
  const [dataLoader, setDataLoader] = useState<boolean>(true);
  const [totalItems, setTotalItems] = useState<number>(1);
  const [deleteLoader, setDeleteLoader] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const handleOpenConfirm = (item: any) => {
    setSelectedItem(item);
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
    setSelectedItem(null);
  };

  const getAllReportedUsers = () => {
    setDataLoader(true);

    allReportedUsersService(searchText, activePage, itemsPerPage)
      .then(({ data }) => {
        setAllUsers(data?.data?.data);
        setDataLoader(false);
        setTotalPage(data?.data?.last_page);
        setTotalItems(data?.data?.total);
        setItemsPerPage(data?.data?.per_page);
        setCurrentPage(data?.data?.current_page);
      })
      .catch((err) => {
        console.log("error", err?.response?.data?.message);
        toastMessage("error", err?.response?.data?.message);
        setDataLoader(false);
      });
  };

  useEffect(() => {
    getAllReportedUsers();
    // eslint-disable-next-line
  }, [searchText, activePage, itemsPerPage]);

  useDebounce(
    () => {
      const trimmedSearch = search.trim();
      if (trimmedSearch === searchText) return;
      setSearchText(trimmedSearch);
      setActivePage(1);
    },
    [search],
    800
  );

  const admin_user_roles = [
    { id: 1, label: "School" },
    { id: 2, label: "Teacher" },
    { id: 3, label: "Parent" },
    { id: 4, label: "Reader" },
    { id: 5, label: "Author" },
    { id: 6, label: "Publisher" },
  ];

  const handleDelete = () => {
    setDeleteLoader(true);
    deleteRequestService(selectedItem?.id)
      .then(({ data }) => {
        toastMessage("success", data?.message);
        setDeleteLoader(false);
        handleCloseConfirm();
        getAllReportedUsers();
      })
      .catch((err) => {
        toastMessage("success", err?.response?.data?.message);
        console.log("err", err?.response?.data?.message);
        setDeleteLoader(false);
      });
  };

  useBreadcrumbs({
    title: "Reported Users",
    path: routeConstant.reportedUsers.path,
  });

  return (
    <div>
      <Box className={styles.topLevelContainer}>
        <Box className={styles.topHeadContainer}>
          <Box>
            <div className={styles.tableHeading}>Reported Users</div>
            <div className={styles.tableSubHeading}>
              All reported accounts lists here.
            </div>
          </Box>
          <Box className={styles.filterContainer}>
            <div className={styles.gridContainer}>
              <Icons.SearchIcon className={styles.searchIcon} />
              <input
                placeholder="Search"
                className={styles.input}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </Box>
        </Box>
        <TableContainer
          component={Paper}
          className={styles.table}
          sx={screenWidth > 990 ? { width: `${screenWidth - 300}px` } : {}}
        >
          <Table aria-label="simple table">
            <TableHead>
              <TableRow className={styles.tableHead}>
                <TableCell>Participent name</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Total Reports</TableCell>
                {/* <TableCell>Published by</TableCell> */}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataLoader ? (
                <SkeletonLoader />
              ) : (
                allUsers?.map((item: any) => {
                  return (
                    <>
                      <TableRow className={styles.tableRow}>
                        <TableCell>
                          <Box display={"flex"} alignItems={"center"}>
                            <Avatar
                              alt={item?.name}
                              src={
                                item?.role === user_roles?.author
                                  ? item?.partner?.profile_picture
                                  : item?.role === user_roles.publisher
                                  ? item?.partner?.publishing_logo
                                  : null
                              }
                            />
                            <div className={styles.titleWrapper}>
                              <span
                                className={styles.tableItem}
                                style={{ marginLeft: "10px" }}
                              >
                                {item?.role === user_roles?.author
                                  ? item?.partner?.full_name
                                  : item?.role === user_roles.publisher
                                  ? item?.partner?.publishing_house
                                  : null}
                              </span>
                              <span>
                                {item?.reports_count &&
                                item?.reports_count.pending > 0 ? (
                                  <Icons.NewIcon></Icons.NewIcon>
                                ) : null}
                              </span>
                            </div>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {
                            admin_user_roles.find(
                              (role) => role.id === Number(item?.role)
                            )?.label
                          }
                        </TableCell>
                        <TableCell>{item?.email}</TableCell>
                        <TableCell>
                          {item?.reports_count ? item?.reports_count?.total : 0}
                        </TableCell>
                        {/* <TableCell>
                          <Box display={"flex"} alignItems={"center"}>
                            <img
                              src={
                                item?.role === user_roles?.author
                                  ? item?.partner?.profile_picture
                                  : item?.role === user_roles.publisher
                                  ? item?.partner?.publishing_logo
                                  : null
                              }
                              alt="book cover"
                              style={{ borderRadius: "50%" }}
                              className={styles.publishedByCover}
                            />
                            <span style={{ marginLeft: "10px" }}>
                              {item?.role === user_roles?.author
                                ? item?.partner?.full_name
                                : item?.role === user_roles.publisher
                                ? item?.partner?.publishing_house
                                : null}
                            </span>
                          </Box>
                        </TableCell> */}

                        <TableCell>
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            className={styles.actionBtn}
                          >
                            <Tooltip title={"View profile"} arrow>
                              <button
                                onClick={() => {
                                  navigate( item?.role === user_roles?.author
                                    ? routeConstant.singleAuthor.path.replace(
                                        ":id",
                                        item?.user_id
                                      )
                                    :
                                    routeConstant.publisherProfile.path.replace(
                                      ":id",
                                      item?.user_id
                                    )
                                  );
                                }}
                              >
                                <RemoveRedEyeIcon />
                              </button>
                            </Tooltip>
                            <Tooltip title={"MOU"} arrow>
                              <button
                                onClick={() => {
                                  navigate(
                                    routeConstant.mouHead.path.replace(
                                      ":id",
                                      item?.user_id
                                    ),
                                    {
                                      state: {
                                        user: item?.publisher
                                          ? item?.publisher
                                          : item?.author,
                                      },
                                    }
                                  );
                                }}
                              >
                                <ArticleIcon />
                              </button>
                            </Tooltip>
                            <Tooltip title={"Report detail"} arrow>
                              <button
                                onClick={() => {
                                  navigate(
                                    routeConstant.reports.path.replace(
                                      ":id",
                                      item?.user_id
                                    ),
                                    {
                                      state: {
                                        user: item?.role === user_roles?.publisher
                                          ? "Publisher"
                                          : "Author",
                                      },
                                    }
                                  );
                                }}
                              >
                                <ReportIcon />
                              </button>
                            </Tooltip>
                            <Tooltip title={"Delete"} arrow>
                              <button onClick={() => handleOpenConfirm(item)}>
                                <DeleteIcon />
                              </button>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!(allUsers?.length > 0) && !dataLoader && (
          <div className={styles.noUserCard}>
            <Icons.NoUsersData />
            <h5>No Data Found</h5>
          </div>
        )}
        {totalPage > 1 && (
          <Pagination
            totalPages={totalPage}
            activePage={activePage}
            setActivePage={setActivePage}
          />
        )}
        {allUsers?.length > 0 ? (
          <ItemsPerPage
            setActivePage={setActivePage}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            totalItems={totalItems}
          ></ItemsPerPage>
        ) : null}

        <Confirmation
          text="You want to delete this users report?"
          open={openConfirm}
          handleClose={handleCloseConfirm}
          handleClick={handleDelete}
          loader={deleteLoader}
        />
      </Box>
    </div>
  );
}

export default ReportedUsers;
